import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        if (!formData.name.trim()) {
          toast.error('Please enter your name');
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name);
        toast.success('Account created successfully!');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left — Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white border-r border-border">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 mb-10 group">
            <div className="w-4 h-4 bg-primary" />
            <span className="text-[12px] font-bold uppercase tracking-[0.1em] text-foreground">Coursework</span>
          </Link>

          <p className="label-swiss mb-2">{isLogin ? 'Sign in' : 'Create account'}</p>
          <h1 className="text-2xl font-bold text-foreground tracking-tight mb-6">
            {isLogin ? 'Welcome back.' : 'Get started.'}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs font-medium">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-9 h-10"
                    data-testid="auth-name-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@school.edu"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-9 h-10"
                  required
                  data-testid="auth-email-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Min. 6 characters"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-9 h-10"
                  required
                  minLength={6}
                  data-testid="auth-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-10 mt-2"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
              <ArrowRight className="w-3.5 h-3.5 ml-2" />
            </Button>
          </form>

          <div className="mt-6 border-t border-border pt-5 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="auth-toggle-btn"
            >
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <span className="font-semibold text-primary">
                {isLogin ? 'Sign up' : 'Sign in'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Right — Brand panel */}
      <div className="hidden lg:flex flex-1 bg-foreground items-center justify-center p-14">
        <div className="max-w-sm">
          <div className="w-6 h-6 bg-primary mb-8" />
          <h2 className="text-4xl font-bold text-white tracking-tight leading-tight mb-5">
            The systematic<br />
            approach to IB.
          </h2>
          <p className="text-white/60 text-sm leading-relaxed mb-8">
            Track your Extended Essay, manage IA deadlines, organize research sources,
            and generate citations — all in one focused workspace.
          </p>
          <div className="space-y-2">
            {['EE Tracking', 'IA Management', 'TOK Support', 'Citation Generator'].map((item) => (
              <div key={item} className="flex items-center gap-2">
                <div className="w-1 h-1 bg-primary" />
                <span className="text-white/70 text-xs uppercase tracking-wide font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
